import {DragAndDrop} from '@github-ui/drag-and-drop'
import {IssueRow, IssuesIndexSecondaryGraphqlQuery} from '@github-ui/list-view-items-issues-prs/IssueRow'
import type {IssueRowSecondaryQuery} from '@github-ui/list-view-items-issues-prs/IssueRowSecondaryQuery'
import {PullRequestRow} from '@github-ui/list-view-items-issues-prs/PullRequestRow'
import type {QUERY_FIELDS} from '@github-ui/list-view-items-issues-prs/Queries'
import {noop} from '@github-ui/noop'
import type {NavigateOptions, To} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {useEffect} from 'react'
import {useQueryLoader} from 'react-relay'

import styles from './RepositoryMilestone.module.css'
import type {ItemNodeType} from './types'

type MilestoneIssuesListPageProps = {
  orderedNodes: ItemNodeType[]
  checkedItems: Map<string, ItemNodeType>
  itemSelected: (id: string, node: ItemNodeType, selected: boolean) => void
  getMetadataHref: (queryField: keyof typeof QUERY_FIELDS, metadataName: string) => string
  getAdditiveMetadataHref: (queryField: keyof typeof QUERY_FIELDS, metadataName: string) => string
  withDragAndDrop: boolean
  scopedRepository: {id: string; name: string; owner: string; is_archived: boolean}
  handleNavigate: (issueNumber: number, to: To, navOptions?: NavigateOptions) => Promise<void>
}

export function MilestoneIssuesListPage({
  orderedNodes,
  checkedItems,
  itemSelected,
  getMetadataHref,
  getAdditiveMetadataHref,
  withDragAndDrop,
  scopedRepository,
  handleNavigate,
}: MilestoneIssuesListPageProps) {
  const navigate = useNavigate()
  const [milestoneIssuesLazyDataRef, loadMilestoneIssuesLazyData] = useQueryLoader<IssueRowSecondaryQuery>(
    IssuesIndexSecondaryGraphqlQuery,
  )
  const hasLazyData = milestoneIssuesLazyDataRef !== null
  useEffect(() => {
    const nodesIds = orderedNodes.map(node => node.id)
    loadMilestoneIssuesLazyData({nodes: nodesIds, includeReactions: false})
  }, [loadMilestoneIssuesLazyData, orderedNodes, hasLazyData])

  const items = orderedNodes?.map((node, index) => {
    const sharedRowData = {
      isActive: false,
      isSelected: node && checkedItems.has(node.id),
      onSelect: (selected: boolean) => node && itemSelected(node.id, node, selected),
      onSelectRow: noop,
      getMetadataHref,
      getAdditiveMetadataHref,
      reactionEmojiToDisplay: {reaction: '', reactionEmoji: ''},
      sortingItemSelected: '',
      scopedRepository,
      metadataRef: milestoneIssuesLazyDataRef,
    }
    if (!withDragAndDrop) {
      if (node.__typename === 'Issue') {
        return (
          <IssueRow
            key={node.id}
            issueKey={node}
            onNavigate={(to: To, navOptions?: NavigateOptions) => handleNavigate(node.number, to, navOptions)}
            {...sharedRowData}
          />
        )
      } else {
        return <PullRequestRow key={node.id} pullRequestKey={node} onNavigate={navigate} {...sharedRowData} />
      }
    }

    return (
      <DragAndDrop.Item
        hideTrigger
        as="li"
        index={index}
        id={node.id}
        key={node.id}
        title={''}
        style={{display: 'flex', width: '100%'}}
        className={styles.dragAndDropItem}
        tabIndex={-1} // tabIndex is handled by useRovingTabIndex for tab navigation
        elementId={`list-view-node-${node.id}`}
      >
        {node.__typename === 'Issue' ? (
          <IssueRow
            key={node.id}
            issueKey={node}
            onNavigate={(to: To, navOptions?: NavigateOptions) => handleNavigate(node.number, to, navOptions)}
            {...sharedRowData}
            as="div"
            role="none"
          />
        ) : (
          <PullRequestRow
            key={node.id}
            pullRequestKey={node}
            onNavigate={navigate}
            {...sharedRowData}
            as="div"
            role="none"
          />
        )}
      </DragAndDrop.Item>
    )
  })
  return <>{items}</>
}
